import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ListChecks } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button-variants';
import { Card, CardContent } from '@/components/ui/card';
import { useSetupStatus } from './useSetupStatus';

/**
 * C-15 — the nudge on the home screen, and the venue's status at a glance.
 *
 * Deliberately self-erasing. It renders only while a gap exists *and* this actor
 * can close one, so a finished venue is never nagged and a waiter is never shown
 * a list of chores they have no permission to do anything about.
 *
 * The setup screen became a wizard, which is a place you *work*; this stayed a
 * place you *look*. Something still has to answer "what is this venue missing"
 * without opening anything, and on the overview — the screen a manager lands on
 * every morning — that is this card. It reads the same probes the wizard does,
 * so the two can never disagree.
 *
 * The link now names the step rather than the screen. `/setup` alone would land
 * on the first unfinished step anyway, but the card has already told the
 * operator which one it means, and arriving somewhere else than the sentence
 * promised is how a nudge stops being trusted.
 */
export default function SetupCard() {
  const { t } = useTranslation();
  const { loading, actionable } = useSetupStatus();

  if (loading || !actionable.length) return null;

  return (
    <Card className="border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50">
      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              {t('setup.checklist.card.title')}
            </p>
            <p className="text-sm text-amber-900 dark:text-amber-200/80">
              {/* `n`, not `count`: an Arabic plural has six categories and this
                  sentence does not need six translations to stay correct. */}
              {t('setup.checklist.card.hint', {
                n: actionable.length,
                first: t(`setup.checklist.steps.${actionable[0].id}.title`),
              })}
            </p>
          </div>
        </div>

        <Link
          to={`/setup/${actionable[0].id}`}
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          {t('setup.checklist.card.cta')}
          <ArrowRight className="h-4 w-4 rtl:rotate-180" />
        </Link>
      </CardContent>
    </Card>
  );
}
